import React, {useState, useEffect} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {partOrderService} from '../../services/api';
import {PencilIcon, TrashIcon} from "@heroicons/react/24/outline";
import {useAuth} from "../../contexts/AuthContext";
import DeleteConfirmationModal from "../../components/modals/DeleteConfirmationModal";
import {toast} from "react-toastify";

const PartOrders = () => {
	const { hasAnyRole } = useAuth();
	const navigate = useNavigate();
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [orderToDelete, setOrderToDelete] = useState(null);
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState({
		                                     status: '',
		                                     search: '',
		                                     supplier: '',
	                                     });

	useEffect(() => {
		const loadOrders = async () => {
			try {
				setLoading(true);
				const data = await partOrderService.getPartOrders(filter);
				console.log('PartOrders', data);
				setOrders(data.data);
			}
			catch (error) {
				console.error("Error fetching part orders:", error);
				// Alert or toast notification could go here
			}
			finally {
				setLoading(false);
			}
		};

		loadOrders();
	}, [filter]);

	const handleFilterChange = (key, value) => {
		setFilter(prev => ({ ...prev, [key]: value }));
	};

	const renderStatusBadge = (status) => {
		const classes = {
			draft: 'bg-gray-100 text-gray-800',
			ordered: 'bg-blue-100 text-blue-800',
			partially_received: 'bg-yellow-100 text-yellow-800',
			received: 'bg-green-100 text-green-800',
			cancelled: 'bg-red-100 text-red-800'
		};

		return (
			<span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${classes[status] || 'bg-gray-100 text-gray-800'}`}>
                {status.replace('_', ' ')}
            </span>
		);
	};

	const suggestOrder = async () => {
		try {
			const suggestedOrder = await partOrderService.suggestOrder();
			navigate('/part-orders/new', { state: { suggestedOrder } });
		}
		catch (error) {
			console.error("Error suggesting order:", error);
			// Alert or toast notification could go here
		}
	};

	const generateCutSheetOrder = async () => {
		try {
			// First get the cut sheet data
			const cutSheetData = await partOrderService.generateCutSheet();
			console.log(cutSheetData);
			navigate('/part-orders/new', { state: { cutSheetData } });
		}
		catch (error) {
			console.error("Error generating cut sheet:", error);
			// Alert or toast notification could go here
		}
	};

	const createOrdersFromCutSheet = async () => {
		try {
			setLoading(true);
			const result = await partOrderService.createOrdersFromCutSheet();
			// Show success message
			alert(`${result.message} - ${result.data.length} orders created`);
			// Refresh orders list
			const data = await partOrderService.getPartOrders(filter);
			setOrders(data.data);
		}
		catch (error) {
			console.error("Error creating orders from cut sheet:", error);
			alert("Failed to create orders from cut sheet");
		}
		finally {
			setLoading(false);
		}
	};

	const handleDeleteClick = (order, e) => {
		e.preventDefault();
		e.stopPropagation();
		setOrderToDelete(order);
		setDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		try {
			setLoading(true);
			await partOrderService.deletePartOrder(orderToDelete.id);

			// Refresh the orders list
			const data = await partOrderService.getPartOrders(filter);
			setOrders(data.data);

			// Show success message
			//alert("Order deleted successfully");
			toast.success('Order deleted successfully');
		}
		catch (error) {
			console.error("Error deleting order:", error);
			toast.error("Failed to delete order");
		}
		finally {
			setDeleteModalOpen(false);
			setOrderToDelete(null);
			setLoading(false);
		}
	};

	return (
		<div className="container mx-auto px-4 py-6">
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-gray-800">Part Orders</h1>
				<div className="flex space-x-3">
					<div className="relative inline-block text-left">
						<button
							type="button"
							className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
							onClick={() => {
								const dropdown = document.getElementById('orderDropdown');
								dropdown.classList.toggle('hidden');
							}}
						>
							Auto Generate
							<svg className="h-5 w-5 ml-2 inline" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
								<path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
							</svg>
						</button>
						<div id="orderDropdown" className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 hidden">
							<div className="py-1" role="menu" aria-orientation="vertical">
								<button onClick={generateCutSheetOrder}
								        className="text-left w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">
									Generate from Cut Sheet (Preview First)
								</button>
								<button onClick={createOrdersFromCutSheet}
								        className="text-left w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">
									Auto Create Orders from Cut Sheet
								</button>
							</div>
						</div>
					</div>
					<button
						onClick={() => navigate('/part-orders/new')}
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
					>
						<svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
							<path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd"/>
						</svg>
						New Order
					</button>
				</div>
			</div>

			<div className="bg-white rounded-lg shadow">
				<div className="px-6 py-4 border-b border-gray-200">
					<h2 className="text-xl font-semibold text-gray-800">Part Orders</h2>
				</div>
				<div className="p-6">
					<div className="flex flex-wrap gap-4 mb-6">
						<div className="w-full md:w-64">
							<input
								className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								placeholder="Search by order number or supplier..."
								value={filter.search}
								onChange={(e) => handleFilterChange('search', e.target.value)}
							/>
						</div>
						<div className="w-full md:w-48">
							<select
								className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								value={filter.status}
								onChange={(e) => handleFilterChange('status', e.target.value)}
							>
								<option value="">All Status</option>
								<option value="DRAFT">Draft</option>
								<option value="ORDERED">Ordered</option>
								<option value="PARTIAL">Partially Received</option>
								<option value="RECEIVED">Received</option>
								<option value="CANCELLED">Cancelled</option>
							</select>
						</div>
						<div className="w-full md:w-48">
							<input
								className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								placeholder="Filter by supplier..."
								value={filter.supplier}
								onChange={(e) => handleFilterChange('supplier', e.target.value)}
							/>
						</div>
						<button
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							onClick={() => setFilter({ status: '', search: '', supplier: '' })}
						>
							Reset Filters
						</button>
					</div>

					{loading ? (
						<div className="flex justify-center items-center h-64">
							<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
						</div>
					) : orders.length === 0 ? (
						<div className="text-center py-8 text-gray-500">
							No part orders found
						</div>
					) : (
						    <div className="overflow-x-auto">
							    <table className="min-w-full divide-y divide-gray-200">
								    <thead className="bg-gray-50">
								    <tr>
									    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
									    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
									    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
									    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Date</th>
									    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Value</th>
									    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
									    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
								    </tr>
								    </thead>
								    <tbody className="bg-white divide-y divide-gray-200">
								    {orders.map((order) => (
									    <tr key={order.id} className="hover:bg-gray-50">
										    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
											    <a href="#" onClick={(e) => {
												    e.preventDefault();
												    navigate(`/part-orders/${order.id}`);
											    }}>
												    {order.order_number}
											    </a>
										    </td>
										    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											    {order?.vendor?.name}
										    </td>
										    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											    {format(new Date(order.order_date), 'MM/dd/yyyy')}
										    </td>
										    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											    {order.expected_delivery_date ? format(new Date(order.expected_delivery_date), 'MM/dd/yyyy') : 'Not specified'}
										    </td>
										    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											    {order.total_value && (
												    <>
													    ${parseFloat(order?.total_value).toFixed(2)}
												    </>
											    )}
										    </td>
										    <td className="px-6 py-4 whitespace-nowrap">
											    {renderStatusBadge(order.status)}
										    </td>
										    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
											    <div className="flex justify-end space-x-2">
												    <button
													    onClick={() => navigate(`/part-orders/${order.id}`)}
													    className="text-blue-600 hover:text-blue-900"
												    >
													    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
														    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
														    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
													    </svg>
												    </button>
												    {order.status === 'draft' && (
													    <button
														    onClick={() => navigate(`/part-orders/edit/${order.id}`)}
														    className="text-gray-600 hover:text-gray-900"
														    disabled={order.status !== 'draft'}
													    >
														    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
															    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
														    </svg>
													    </button>
												    )}


												    {hasAnyRole(['procurement']) && (
													    <>
														    {order.status === 'draft' && (
															    <button
																    onClick={(e) => handleDeleteClick(order, e)}
																    className="text-red-600 hover:text-red-900"
																    title="Delete Order"
															    >
																    <TrashIcon className="h-5 w-5"/>
															    </button>
														    )}
													    </>
												    )}

												    {hasAnyRole(['admin']) && (
													    <>
														    <button
															    onClick={(e) => handleDeleteClick(order, e)}
															    className="text-red-600 hover:text-red-900"
															    title="Delete Order"
														    >
															    <TrashIcon className="h-5 w-5"/>
														    </button>
													    </>
												    )}
											    </div>
										    </td>
									    </tr>
								    ))}
								    </tbody>
							    </table>
						    </div>
					    )}
				</div>
			</div>
			<DeleteConfirmationModal
				isOpen={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				onConfirm={confirmDelete}
				title={"Delete Order"}
				message={orderToDelete
				         ? `Are you sure you want to delete order ${orderToDelete.order_number}? This action cannot be undone.`
				         : "Are you sure you want to delete this order? This action cannot be undone."}
			/>
		</div>
	);
};

export default PartOrders;
